/**
 * Lightweight API client wrapper.
 * Phase 2 以降で SRP 認証ヘッダ / CSRF / トークン更新を追加する。
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    const body = await safeJson(res)
    throw new ApiError(res.status, `GET ${path} failed: ${res.status}`, body)
  }

  return (await res.json()) as T
}

export interface ApiPostOptions {
  /** Bearer token (Authorization ヘッダに `Bearer <token>` で送信) */
  bearerToken?: string
}

export async function apiPost<TResponse, TRequest extends object>(
  path: string,
  payload: TRequest,
  options: ApiPostOptions = {},
): Promise<TResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  if (options.bearerToken) {
    headers.Authorization = `Bearer ${options.bearerToken}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const body = await safeJson(res)
    throw new ApiError(res.status, `POST ${path} failed: ${res.status}`, body)
  }

  // 204 No Content / Content-Length: 0 のときは JSON parse すると SyntaxError になるので
  // undefined を返す (TResponse が void のときに対応)。それ以外は通常通り JSON を返す。
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as TResponse
  }

  return (await res.json()) as TResponse
}

export interface ApiPutOptions {
  /** Bearer token (Authorization ヘッダに `Bearer <token>` で送信) */
  bearerToken?: string
}

/**
 * PUT リクエスト。recovery/reset のように Bearer recoverySessionToken で
 * 認可する用途を想定 (cookie の access token は Phase 4 で導入予定)。
 *
 * 204 No Content の場合は body を読まず undefined を返す。
 */
export async function apiPut<TResponse, TRequest extends object>(
  path: string,
  payload: TRequest,
  options: ApiPutOptions = {},
): Promise<TResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  if (options.bearerToken) {
    headers.Authorization = `Bearer ${options.bearerToken}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const body = await safeJson(res)
    throw new ApiError(res.status, `PUT ${path} failed: ${res.status}`, body)
  }

  if (res.status === 204) {
    return undefined as TResponse
  }

  return (await res.json()) as TResponse
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return undefined
  }
}

export interface HealthResponse {
  status: 'ok'
  service: 'ookey-api'
  version: string
  timestamp: string
  uptime: number
}

export const fetchHealth = (): Promise<HealthResponse> => apiGet<HealthResponse>('/health')
